import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarAPI, projectsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  addDays,
  differenceInDays,
  startOfWeek,
  endOfWeek,
  format,
  isToday,
  parseISO,
  startOfDay,
  isBefore,
  isAfter,
  min as minDate,
  max as maxDate,
  addWeeks,
  subWeeks,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  CalendarDays,
  GanttChart as GanttIcon,
  ZoomIn,
  ZoomOut,
  Search,
} from 'lucide-react';
import { Input } from '../components/ui/input';

const PRIORITY_COLORS = {
  high: { bar: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' },
  medium: { bar: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-100' },
  low: { bar: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-100' },
};

const STATUS_STYLES = {
  todo: 'opacity-70',
  in_progress: 'opacity-100',
  completed: 'opacity-50',
};

export default function GanttChart() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewWeeks, setViewWeeks] = useState(4);
  const [startDate, setStartDate] = useState(null);
  const [filterProject, setFilterProject] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes] = await Promise.all([
        calendarAPI.getTasks(),
        projectsAPI.list(),
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);

      // Auto-fit: start view from the earliest task due date (or today if no tasks)
      const tasksWithDates = tasksRes.data.filter(t => t.due_date);
      if (tasksWithDates.length > 0) {
        const earliest = tasksWithDates.reduce((min, t) =>
          new Date(t.due_date) < new Date(min.due_date) ? t : min
        );
        setStartDate(startOfWeek(subWeeks(parseISO(earliest.due_date), 1), { weekStartsOn: 1 }));
      } else {
        setStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
      }
    } catch (err) {
      console.error('Failed to fetch gantt data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const endDate = useMemo(() => addDays(startDate, viewWeeks * 7 - 1), [startDate, viewWeeks]);
  const totalDays = viewWeeks * 7;

  const days = useMemo(() => {
    return Array.from({ length: totalDays }, (_, i) => addDays(startDate, i));
  }, [startDate, totalDays]);

  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(t => t.due_date);
    if (filterProject !== 'all') {
      filtered = filtered.filter(t => t.project_id === filterProject);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => t.title?.toLowerCase().includes(term) || t.project_name?.toLowerCase().includes(term));
    }
    return filtered;
  }, [tasks, filterProject, searchTerm]);

  const groupedByProject = useMemo(() => {
    const groups = {};
    filteredTasks.forEach(task => {
      const pid = task.project_id;
      if (!groups[pid]) {
        groups[pid] = {
          projectName: task.project_name || 'Unknown',
          projectId: pid,
          tasks: [],
        };
      }
      groups[pid].tasks.push(task);
    });
    Object.values(groups).forEach(g => {
      g.tasks.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
    });
    return Object.values(groups);
  }, [filteredTasks]);

  const getBarPosition = (task) => {
    const dueDate = startOfDay(parseISO(task.due_date));
    const taskStart = addDays(dueDate, -2);
    const taskEnd = dueDate;

    const visibleStart = maxDate([taskStart, startDate]);
    const visibleEnd = minDate([taskEnd, endDate]);

    if (isAfter(visibleStart, endDate) || isBefore(visibleEnd, startDate)) {
      return null;
    }

    const leftDays = differenceInDays(visibleStart, startDate);
    const widthDays = differenceInDays(visibleEnd, visibleStart) + 1;

    return {
      left: `${(leftDays / totalDays) * 100}%`,
      width: `${(widthDays / totalDays) * 100}%`,
    };
  };

  const goToPrevious = () => setStartDate(prev => subWeeks(prev, 1));
  const goToNext = () => setStartDate(prev => addWeeks(prev, 1));
  const goToToday = () => setStartDate(startOfWeek(new Date(), { weekStartsOn: 1 }));

  if (loading || !startDate) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="gantt-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight">
              Project Timeline
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gantt view of tasks across all projects
            </p>
          </div>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goToPrevious} data-testid="gantt-prev">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday} data-testid="gantt-today">
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={goToNext} data-testid="gantt-next">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium ml-2">
                  {format(startDate, 'MMM d')} – {format(endDate, 'MMM d, yyyy')}
                </span>
              </div>

              <div className="flex items-center gap-2 sm:ml-auto">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-9 w-48"
                    data-testid="gantt-search-input"
                  />
                </div>
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger className="w-48" data-testid="gantt-project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="flex items-center gap-1 border rounded-md">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setViewWeeks(w => Math.max(2, w - 1))}
                    data-testid="gantt-zoom-in"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <span className="text-xs px-1 min-w-[3rem] text-center">{viewWeeks}w</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setViewWeeks(w => Math.min(12, w + 1))}
                    data-testid="gantt-zoom-out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gantt Chart */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {groupedByProject.length === 0 ? (
              <div className="py-16 text-center">
                <GanttIcon className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No tasks with due dates</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Create tasks with due dates to see them on the timeline
                </p>
              </div>
            ) : (
              <div className="min-w-[800px]">
                {/* Timeline Header */}
                <div className="flex border-b border-border sticky top-0 bg-background z-10">
                  <div className="w-56 min-w-[224px] flex-shrink-0 px-4 py-2 border-r border-border">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Project / Task
                    </span>
                  </div>
                  <div className="flex-1 flex">
                    {days.map((day, i) => {
                      const isWeekStart = day.getDay() === 1;
                      const isTodayDate = isToday(day);
                      return (
                        <div
                          key={day.toISOString()}
                          className={`flex-1 text-center py-2 text-[10px] border-r border-border/30 relative
                            ${isTodayDate ? 'bg-primary/10 font-bold' : ''}
                            ${isWeekStart ? 'border-l border-border' : ''}
                            ${day.getDay() === 0 || day.getDay() === 6 ? 'bg-muted/30' : ''}
                          `}
                        >
                          <div className="text-muted-foreground">{format(day, 'EEE')}</div>
                          <div className={isTodayDate ? 'text-primary' : ''}>{format(day, 'd')}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Project Groups */}
                {groupedByProject.map((group) => (
                  <div key={group.projectId} data-testid={`gantt-project-${group.projectId}`}>
                    {/* Project Header */}
                    <div
                      className="flex border-b border-border bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
                      onClick={() => navigate(`/projects/${group.projectId}`)}
                    >
                      <div className="w-56 min-w-[224px] flex-shrink-0 px-4 py-2 border-r border-border">
                        <span className="text-sm font-semibold">{group.projectName}</span>
                        <span className="text-xs text-muted-foreground ml-2">({group.tasks.length})</span>
                      </div>
                      <div className="flex-1 relative">
                        {/* Today indicator line */}
                        {days.some(d => isToday(d)) && (
                          <div
                            className="absolute top-0 bottom-0 w-px bg-primary z-10"
                            style={{
                              left: `${(differenceInDays(startOfDay(new Date()), startDate) / totalDays) * 100}%`,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Task Rows */}
                    {group.tasks.map((task) => {
                      const barPos = getBarPosition(task);
                      const colors = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
                      const statusStyle = STATUS_STYLES[task.status] || '';

                      return (
                        <div
                          key={task.task_id}
                          className="flex border-b border-border/50 hover:bg-muted/20 transition-colors group"
                          data-testid={`gantt-task-${task.task_id}`}
                        >
                          <div className="w-56 min-w-[224px] flex-shrink-0 px-4 py-2 border-r border-border">
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.bar}`}
                              />
                              <span className="text-sm truncate" title={task.title}>
                                {task.title}
                              </span>
                            </div>
                            <div className="text-[10px] text-muted-foreground ml-4 mt-0.5">
                              Due {format(parseISO(task.due_date), 'MMM d')}
                              {task.status === 'completed' && ' — Done'}
                            </div>
                          </div>
                          <div className="flex-1 relative py-1">
                            {/* Grid lines */}
                            {days.map((day, i) => (
                              <div
                                key={day.toISOString()}
                                className={`absolute top-0 bottom-0 border-r border-border/10
                                  ${day.getDay() === 1 ? 'border-border/30' : ''}
                                  ${day.getDay() === 0 || day.getDay() === 6 ? 'bg-muted/15' : ''}
                                `}
                                style={{
                                  left: `${(i / totalDays) * 100}%`,
                                  width: `${(1 / totalDays) * 100}%`,
                                }}
                              />
                            ))}

                            {/* Today line */}
                            {days.some(d => isToday(d)) && (
                              <div
                                className="absolute top-0 bottom-0 w-px bg-primary/60 z-10"
                                style={{
                                  left: `${(differenceInDays(startOfDay(new Date()), startDate) / totalDays) * 100}%`,
                                }}
                              />
                            )}

                            {/* Task Bar */}
                            {barPos && (
                              <div
                                className={`absolute top-1/2 -translate-y-1/2 h-6 rounded-sm cursor-pointer
                                  ${colors.bar} ${statusStyle}
                                  hover:brightness-110 transition-all shadow-sm
                                  ${task.status === 'completed' ? 'bg-opacity-40' : ''}
                                `}
                                style={{
                                  left: barPos.left,
                                  width: barPos.width,
                                  minWidth: '8px',
                                }}
                                title={`${task.title} — Due: ${format(parseISO(task.due_date), 'MMM d, yyyy')}`}
                                onClick={() => navigate(`/projects/${task.project_id}`)}
                              >
                                <span className="text-[10px] text-white font-medium px-1.5 truncate block leading-6">
                                  {task.title}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
          <span className="font-medium uppercase tracking-wider">Priority:</span>
          {Object.entries(PRIORITY_COLORS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${val.bar}`} />
              <span className="capitalize">{key}</span>
            </div>
          ))}
          <span className="mx-2">|</span>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-3 rounded-sm bg-muted-foreground/30" />
            <span>Completed</span>
          </div>
        </div>
      </div>
    </Layout>
  );
}
